/* eslint-disable */
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import isNil from 'lodash/isNil';

import {
  FLAT_ROOF_COVERING_OPTIONS,
  LEAD_STATE,
  OFFER_PRODUCT_LINE,
  OPTIONAL_DEFAULT_VALUES,
  ROOF_COVERING_OPTIONS,
  ROOF_COVERING_SPECIFIC_TYPE_OPTIONS,
  ROOF_TYPE,
  VALIDATION_MESSAGE,
} from '../../config';

export * from './measurementProtocol';

export const DECIMAL_NUMBER_VALIDATION = {
  NO_VALIDATION: '',
  NO_DECIMALS: 'noDecimal',
  ONE_DECIMAL: 'oneDecimal',
  TWO_DECIMAL: 'twoDecimal',
};

export const DECIMAL_DEFAULT_MAX_LENGTH = 8;
export const INTEGER_DEFAULT_MAX_LENGTH = 10;

const VALIDATION_REGEXES = {
  isValidEmailAddressRegExp:
    // eslint-disable-next-line no-control-regex
    /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/,
  isIntNumberRegExp: /^[0-9]*[0-9][0-9]*$/,
  isNumberRegExp: /^\d+(,\d*)?$|^\d*(\.\d+)?$/,
  isNumberRegExpNoDecimal: /^\d+$/,
  isNumberRegExpOneDecimal: /^(?![,\.]$)\d*[,\.]?\d{0,1}$/,
  isNumberRegExpTwoDecimal: /^(?![,\.]$)\d*[,\.]?\d{0,2}$/,
};

export const validateTask = value => {
  if (!value) {
    return 'validation.required';
  }
  if (value.trim().length === 0) {
    return 'validation.emptyTask';
  }
  return null;
};

export const isStringUuid = uuidToTest =>
  VALIDATION_REGEXES.isUuid.test(uuidToTest);

export const formatToDotlessNumberString = value => {
  return value && value.replace(/\./g, '').replace(',', '.');
};

export const validateRequired = value => {
  if (!value) {
    return 'validation.required';
  }
  return null;
};

export const validateMeasurementProtocolRequired = value => {
  if (value === undefined || value === null) {
    return 'validation.required';
  }
  return null;
};

export const validateLeadIdRequired = value => {
  if (!value?.id) {
    return 'validation.required';
  }
  return null;
};

export const validateZip = value => {
  if (value?.length > 5) {
    return 'validation.lengthZip';
  }
  return validateNumberRequired(value);
};

export const validateStartEndDateTimes = ({
  startTime,
  endTime,
  startDate,
  endDate,
}) => {
  if (
    !isSameDay(new Date(startTime), new Date(endTime)) &&
    !isAfter(new Date(endTime), new Date(startTime))
  ) {
    return VALIDATION_MESSAGE.DATE_END_BEFORE_START;
  }
  if (
    isAfter(new Date(startTime), new Date(endTime)) ||
    isAfter(new Date(startDate), new Date(endDate))
  ) {
    return VALIDATION_MESSAGE.DATE_END_BEFORE_START;
  }

  return null;
};

export const validateEmailAddresses = values => {
  let errors = [];
  values.forEach(({ value }, i) => {
    if (
      !value &&
      !VALIDATION_REGEXES.isValidEmailAddressRegExp.test(value?.toLowerCase())
    ) {
      errors.push('validation.invalidEmailAddress');
    } else {
      errors.push(null);
    }
  });
  return errors;
};

export const validateExistContact = (values, formValues, personType) => {
  const phoneValues = personType
    ? formValues[personType]?.phoneNumbers
    : formValues?.phoneNumbers;
  const emailValues = personType
    ? formValues[personType]?.emailAddresses
    : formValues?.emailAddresses;
  if (
    personType === PERSON_FIELD_TYPE.CUSTOMER &&
    (!phoneValues ||
      !phoneValues[0]?.value ||
      !emailValues ||
      !emailValues[0]?.value)
  ) {
    return 'validation.bothRequired';
  }
  if (
    (!phoneValues || !phoneValues[0]?.value) &&
    (!emailValues || !emailValues[0]?.value)
  ) {
    return 'validation.oneRequired';
  }
  return null;
};

export const validateIntNumber = value => {
  if (value && !VALIDATION_REGEXES.isIntNumberRegExp.test(value)) {
    return 'validation.onlyIntegerNumbers';
  }
  return null;
};

export const validateIntNumberRequired = value => {
  if (!value) {
    return 'validation.required';
  }
  if (value && !VALIDATION_REGEXES.isIntNumberRegExp.test(value)) {
    return 'validation.onlyIntegerNumbers';
  }
  return null;
};

export const validateNumber = value => {
  if (value && !VALIDATION_REGEXES.isNumberRegExp.test(value)) {
    return 'validation.numberRequired';
  }
  return null;
};

export const validateNumberRequired = value => {
  if (!value) {
    return 'validation.required';
  }
  if (value && !VALIDATION_REGEXES.isNumberRegExp.test(value)) {
    return 'validation.numberRequired';
  }
  return null;
};

// If you use this add the validation key to the translation file
const validateIntNumberMinToMax = (min, max, isRequired) => value => {
  if (isRequired && !value) {
    return `validation.onlyIntegerNumbersFrom${min}to${max}`;
  }

  if (
    (value || value === 0) &&
    (!VALIDATION_REGEXES.isIntNumberRegExp.test(value) ||
      (min !== undefined && value < min) ||
      (max !== undefined && value > max))
  ) {
    return `validation.onlyIntegerNumbersFrom${min}to${max}`;
  }

  return null;
};

// If you use this add the validation key to the translation file
const validateIntNumberMin = (min, isRequired) => value => {
  if (isRequired && !value) {
    return `validation.onlyIntegerNumberFrom${min}`;
  }

  if (
    value &&
    (!VALIDATION_REGEXES.isIntNumberRegExp.test(value) ||
      (min !== undefined && value < min))
  ) {
    return `validation.onlyIntegerNumberFrom${min}`;
  }

  return null;
};

export const validateNumberMinToMax =
  (min, max, variant = DECIMAL_NUMBER_VALIDATION.NO_VALIDATION, isRequired) =>
  value => {
    if (isRequired && !value) {
      return `validation.onlyNumbersFrom${min}to${max}${variant}`;
    }
    let regex = VALIDATION_REGEXES.isIntNumberRegExp;

    if (variant === DECIMAL_NUMBER_VALIDATION.NO_DECIMALS) {
      regex = VALIDATION_REGEXES.isNumberRegExpNoDecimal;
    } else if (variant === DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL) {
      regex = VALIDATION_REGEXES.isNumberRegExpOneDecimal;
    } else if (variant === DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL) {
      regex = VALIDATION_REGEXES.isNumberRegExpTwoDecimal;
    }

    const valueAsFloatNumber =
      (value || value === 0) && parseFloat(value.toString().replace(',', '.'));

    // For Values with no decimals we also want to allow trailing zeros after comma
    let testValue = value;
    if (variant === DECIMAL_NUMBER_VALIDATION.NO_DECIMALS && value) {
      const valueStr = value.toString();
      if (/[,\.](0+)$/.test(valueStr)) {
        testValue = valueStr.replace(/[,\.](0+)$/, '');
      }
    }

    if (
      (value || value === 0) &&
      (!regex.test(testValue) ||
        (min !== undefined && valueAsFloatNumber < min) ||
        (max !== undefined && valueAsFloatNumber > max))
    ) {
      return `validation.onlyNumbersFrom${min}to${max}${variant}`;
    }
    return null;
  };

export const validateNumberMin =
  (
    min,
    equal = true,
    variant = DECIMAL_NUMBER_VALIDATION.NO_VALIDATION,
    isRequired,
  ) =>
  value => {
    if (isRequired && !value) {
      return `validation.onlyNumbersFrom${min}${variant}`;
    }

    let regex = VALIDATION_REGEXES.isIntNumberRegExp;

    if (variant === DECIMAL_NUMBER_VALIDATION.NO_DECIMALS) {
      regex = VALIDATION_REGEXES.isNumberRegExpNoDecimal;
    } else if (variant === DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL) {
      regex = VALIDATION_REGEXES.isNumberRegExpOneDecimal;
    } else if (variant === DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL) {
      regex = VALIDATION_REGEXES.isNumberRegExpTwoDecimal;
    }

    const valueAsFloatNumber =
      value && parseFloat(value.toString().replace(',', '.'));

    if (
      value &&
      (!regex.test(value) ||
        (min !== undefined && equal
          ? valueAsFloatNumber < min
          : valueAsFloatNumber <= min))
    ) {
      return `validation.onlyNumbersFrom${min}${variant}`;
    }

    return null;
  };

export const validateRequiredIntNumber1to5 = validateIntNumberMinToMax(
  1,
  5,
  true,
);

export const validateRequiredIntNumber1to10 = validateIntNumberMinToMax(
  1,
  10,
  true,
);

export const validateRequiredIntNumber1to50 = validateIntNumberMinToMax(
  1,
  50,
  true,
);

export const validateIntNumberMin0Required = validateIntNumberMin(0, true);
export const validateIntNumberMin0 = validateIntNumberMin(0, true);
export const validateIntNumberMin0Optional = validateIntNumberMin(1, false);
export const validateIntNumberMin1 = validateIntNumberMin(1, true);
export const validateIntNumberMin11 = validateIntNumberMin(11, true);
export const validateIntNumberTrueMin0Optional = validateIntNumberMin(0, false);
export const validateIntNumberMin10 = validateIntNumberMin(10, true);
export const validateIntNumber0to5 = validateIntNumberMinToMax(0, 5);
export const validateIntNumber0to4Required = validateIntNumberMinToMax(
  0,
  4,
  true,
);
export const validateIntNumber1to5 = validateIntNumberMinToMax(1, 5);
export const validateIntNumber1to4Required = validateIntNumberMinToMax(
  1,
  4,
  true,
);
export const validateIntNumber8to75 = validateIntNumberMinToMax(8, 75);
export const validateIntNumber8to67Required = validateIntNumberMinToMax(
  8,
  67,
  true,
);
export const validateIntNumber28to56Required = validateIntNumberMinToMax(
  28,
  56,
  true,
);
export const validateIntNumber12to16Required = validateIntNumberMinToMax(
  12,
  16,
  true,
);
export const validateIntNumber1to1Required = validateIntNumberMinToMax(
  1,
  1,
  true,
);
export const validateIntNumber1to67Required = validateIntNumberMinToMax(
  1,
  67,
  true,
);
export const validateIntNumber0to10 = validateIntNumberMinToMax(0, 10);
export const validateIntNumber0to90 = validateIntNumberMinToMax(0, 90);
export const validateIntNumber0to90Required = validateIntNumberMinToMax(
  0,
  90,
  true,
);
export const validateIntNumber1to10 = validateIntNumberMinToMax(1, 10);
export const validateIntNumber10to30required = validateIntNumberMinToMax(
  10,
  30,
  true,
);
export const validateIntNumber10to30 = validateIntNumberMinToMax(10, 30, false);
export const validateIntNumberBiggerThan15 = validateIntNumberMinToMax(
  15,
  undefined,
  false,
);
export const validateIntNumberBiggerThan15Required = validateIntNumberMinToMax(
  15,
  undefined,
  true,
);
export const validateIntNumber1to50 = validateIntNumberMinToMax(1, 50);
export const validateIntNumber0to100 = validateIntNumberMinToMax(0, 100);
export const validateIntNumber10to100 = validateIntNumberMinToMax(10, 100);
export const validateIntNumber0to1000 = validateIntNumberMinToMax(0, 1000);
export const validateIntNumber0to20Required = validateIntNumberMinToMax(
  0,
  20,
  true,
);
export const validateIntNumber0to2000 = validateIntNumberMinToMax(0, 2000);
export const validateIntNumber0to2000Required = validateIntNumberMinToMax(
  0,
  2000,
  true,
);
export const validateIntNumber0to100k = validateIntNumberMinToMax(0, 100000);
export const validateIntNumber0to300k = validateIntNumberMinToMax(0, 300000);
export const validateFloatNumberOneDecimal0to100 = validateNumberMinToMax(
  0,
  100,
  DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL,
);
export const validateFloatNumberOneDecimal0to150 = validateNumberMinToMax(
  0,
  150,
  DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL,
);
export const validateIntNumber1to100 = validateIntNumberMinToMax(1, 100);
export const validateIntNumber1to100Required = validateIntNumberMinToMax(
  1,
  100,
  true,
);
export const validateFloatNumberOneDecimal1to100 = validateNumberMinToMax(
  1,
  100,
  DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL,
);
export const validateFloatNumberOneDecimal1to100Required =
  validateNumberMinToMax(1, 100, DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL, true);
export const validateFloatNumberOneDecimal0to100Required =
  validateNumberMinToMax(0, 100, DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL, true);

export const validateFloatNumberOneDecimal0to150Required =
  validateNumberMinToMax(0, 150, DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL, true);

export const validateFloatNumberOneDecimal10to100 = validateNumberMinToMax(
  10,
  100,
  DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL,
);
export const validateFloatNumberOneDecimal10to100Required =
  validateNumberMinToMax(10, 100, DECIMAL_NUMBER_VALIDATION.ONE_DECIMAL, true);
export const validateFloatNumberTwoDecimal0to100 = validateNumberMinToMax(
  0,
  100,
  DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL,
);
export const validateFloatNumberTwoDecimal0to100Required =
  validateNumberMinToMax(0, 100, DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL, true);
export const validateFloatNumberTwoDecimal1to100 = validateNumberMinToMax(
  1,
  100,
  DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL,
);
export const validateFloatNumberTwoDecimal0to1000 = validateNumberMinToMax(
  0,
  1000,
  DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL,
);
export const validateFloatNumberTwoDecimal0to1000Required =
  validateNumberMinToMax(0, 1000, DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL, true);
export const validateFloatNumberTwoDecimal0to100k = validateNumberMinToMax(
  0,
  100000,
  DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL,
);
export const validateFloatNumberTwoDecimal0to100kRequired =
  validateNumberMinToMax(
    0,
    100000,
    DECIMAL_NUMBER_VALIDATION.TWO_DECIMAL,
    true,
  );

export const validateTextMaxLength50 = value => {
  if (value && value.length > 50) {
    return 'validation.textLength';
  }
  return null;
};

export const validateTextMaxLength80 = value => {
  if (value && value.length > 80) {
    return 'validation.textLength80';
  }
  return null;
};

export const validateTextMaxLength100 = value => {
  if (value && value.length > 100) {
    return 'validation.validate0to100';
  }
  return null;
};

export const validateTextMaxLength200 = value => {
  if (value && value.length > 200) {
    return 'validation.validate0to200';
  }
  return null;
};

export const validateRequiredPositiveNumber = value => {
  if (
    !value ||
    (value && value <= 0) ||
    !VALIDATION_REGEXES.isNumberRegExp.test(value)
  ) {
    return 'validation.positiveNumberRequired';
  }
  return null;
};

export const validatePositiveNumber = value => {
  if (
    !value ||
    (value && value <= 0) ||
    !VALIDATION_REGEXES.isNumberRegExp.test(value)
  ) {
    return 'validation.positiveNumberRequired';
  }
  return null;
};

export const validateRequiredTextMaxLength50 = value => {
  if (!value || (value && value.length > 50)) {
    return 'validation.textLengthRequired50';
  }
  return null;
};

export const validateRequiredPositiveIntegerNumberWithAdditionalDependencies = (
  value,
  ...additionalValues
) => {
  const regex = VALIDATION_REGEXES.isNumberRegExpNoDecimal;

  const maxLengthConditionHasError =
    value && validateRequiredTextMaxLength100(value);
  const mainConditionHasError =
    !value || (value && value <= 0) || (value && !regex.test(value));
  const additionalValuesAllowValidation = additionalValues.some(
    additionalValue => !additionalValue,
  );

  if (
    !additionalValuesAllowValidation &&
    (mainConditionHasError || maxLengthConditionHasError)
  ) {
    return 'validation.onlyIntegerNumberFrom1';
  }
  return null;
};

export const validateRequiredTextWithAdditionalDependencies = (
  value,
  ...additionalValues
) => {
  const maxLengthConditionHasError =
    value && validateRequiredTextMaxLength100(value);
  const additionalValuesAllowValidation = additionalValues.some(
    additionalValue => !additionalValue,
  );

  if (!additionalValuesAllowValidation && maxLengthConditionHasError) {
    return 'validation.textLengthRequired100';
  }
  return null;
};

export const validateRequiredTextMaxLength100 = value => {
  if (!value || (value && value.length > 100)) {
    return 'validation.textLengthRequired100';
  }
  return null;
};

export const validateRequiredTextMaxLength200 = value => {
  if (!value || (value && value.length > 200)) {
    return 'validation.textLengthRequired200';
  }
  return null;
};

export const validateRequiredTextMaxLength500 = value => {
  if (!value || (value && value.length > 500)) {
    return 'validation.textLengthRequired500';
  }
  return null;
};

export const validateNonRequiredTextMaxLength500 = value => {
  if (value && value.length > 500) {
    return 'validation.textLengthNonRequired500';
  }
  return null;
};

export const validateTextMaxLength1500 = value => {
  if (value && value.length > 1500) {
    return 'validation.textLength1500';
  }
  return null;
};

export const validateTextMaxLength1500Required = value => {
  if (!value) {
    return 'validation.required';
  }
  if (value && value.length > 1500) {
    return 'validation.textLength1500';
  }
  return null;
};

export const validateTextMaxLength500 = value => {
  if (!value) {
    return 'validation.required';
  }
  if (value.length > 500) {
    return 'validation.textLength1500';
  }
  return null;
};

export const validateLeadEditPhaseChange = (
  newLeadphase,
  formValues,
  oldLeadPhase,
) => {
  const leadState = formValues?.state;

  if (leadState === LEAD_STATE.OPEN || newLeadphase === oldLeadPhase) {
    return null;
  }
  return 'validation.leadPhaseChangeOnlyAllowedForOpenLeads';
};

export const validateValueMustBeTrue = value => {
  if (value !== true) {
    return 'validation.validateValueMustBeTrue';
  }
  return null;
};

export const validateTrueOrFalse = value => {
  if (value !== true && value !== false) {
    return 'validation.required';
  }
  return null;
};
export const validateTrueOrFalseOptional = value => {
  if (value == null) {
    return null;
  }
  if (value !== true && value !== false) {
    return 'validation.required';
  }
  return null;
};

export const validateAudioRecordDescription = (value, audio, isNote) => {
  if (isNote) {
    return null;
  }
  return value && audio ? null : 'validation.required';
};

export const validateIBAN = value => {
  let regex = new RegExp(/^[A-Z]{2}\d{2}[A-Z\d]{4}\d{7}([A-Z\d]?){0,16}$/gi);

  if (!value) {
    return 'validation.required';
  }

  if (value && !regex.test(value)) {
    return 'validation.validateIBAN';
  } else {
    return null;
  }
};

export const validateBIC = value => {
  let regex = new RegExp(/^[A-Z0-9]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/gi);

  if (!value) {
    return 'validation.required';
  }

  if (value && !regex.test(value)) {
    return 'validation.validateBIC';
  }
  return null;
};

export const validateDateOfBirth = value => {
  if (!value) {
    return 'validation.required';
  }

  if (
    isAfter(new Date(value), new Date()) ||
    isSameDay(new Date(value), new Date())
  ) {
    return 'validation.validateDateOfBirth';
  }

  return null;
};

export const validateEmailAddress = value => {
  if (!value) {
    return 'validation.required';
  }

  if (
    !VALIDATION_REGEXES.isValidEmailAddressRegExp.test(value?.toLowerCase())
  ) {
    return 'validation.invalidEmailAddress';
  }
  return null;
};

export const validateModuleCount = (value, values) => {
  const productLine = values.ag_basis_premium;

  if (productLine === OFFER_PRODUCT_LINE.M_TEC_BIG) {
    return validateIntNumber28to56Required(value);
  }
  if (productLine === OFFER_PRODUCT_LINE.E2_ACTION_PACKAGE) {
    return validateIntNumber12to16Required(value);
  }
  return validateIntNumber8to67Required(value);
};

export const validateOfferStorageAmount = (value, values) => {
  const isIntNumber = validateIntNumber(value);
  if (!value) {
    return 'validation.required';
  }
  if (isIntNumber) {
    return isIntNumber;
  }
  if (values.productLine === OFFER_PRODUCT_LINE.E2_ACTION_PACKAGE) {
    return validateIntNumber1to1Required(value);
  }

  if (values.speicher_anzahl && values.speicher2_anzahl) {
    if (Number(values.speicher_anzahl) + Number(values.speicher2_anzahl) > 10) {
      return 'validation.offerMaxStorage10';
    }
  }

  return validateIntNumber1to4Required(value);
};

export const validateOnlyOnePackage = (field1, field2) => (value, values) => {
  if (!value) {
    return 'validation.required';
  }
  if (
    values?.[field1] === OPTIONAL_DEFAULT_VALUES.NORMAL &&
    values?.[field2] === OPTIONAL_DEFAULT_VALUES.NORMAL
  ) {
    return `validation.validateOnlyOnePackage.${field1}`;
  }
  return null;
};

export const validateOfferStorageNestedSection = (__, values) => {
  if (!(values.speicher || values.speicher_anzahl)) {
    return 'validation.atLeastOneRequired';
  }
  return null;
};

export const validateOfferRoofNestedSection = (__, values) => {
  if (!(values.d1_hb || values.d1_lm || values.d1_sm)) {
    return 'validation.atLeastOneRequired';
  }
  return null;
};

export const validateNestedEntity2Field =
  (fieldName1, fieldName2, validFunction) => (value, values) => {
    // if (!values[fieldName1] && !values[fieldName2]) {
    //   return null;
    // }
    return validFunction(value, values);
  };

export const validateNestedEntity3Field =
  (fieldName1, fieldName2, fieldName3, validFunction) => (value, values) => {
    // if (!values[fieldName1] && !values[fieldName2] && !values[fieldName3]) {
    //   return null;
    // }
    return validFunction(value, values);
  };

export const validateDiscountPercent = activeField => (value, values) => {
  if (!value) {
    return null;
  }
  const transformedString = formatToDotlessNumberString(value);
  const isNoNumber = validateNumber(transformedString);

  if (isNoNumber) {
    return isNoNumber;
  }
  const transformedValue = parseFloat(transformedString);
  if (activeField === 'ag_rab_prozent') {
    if (transformedValue > 10) {
      return 'validation.offer.discountHigherThan10';
    }
  }
  return null;
};

export const validateDiscountTotal = activeField => (value, values) => {
  if (!value) {
    return null;
  }
  const transformedString = formatToDotlessNumberString(values.ag_rab_prozent);
  const isNoNumber = validateNumber(transformedString);

  if (isNoNumber) {
    return isNoNumber;
  }
  const transformedValue = parseFloat(transformedString);

  if (activeField === 'ag_rab_wert') {
    if (transformedValue > 10) {
      return 'validation.offer.discountHigherThan10';
    }
  }
  return null;
};

export const validateTileMeasurement = (
  value,
  values,
  __,
  additionalInformation,
) => {
  if (value) {
    return validateNumber(value);
  }
  if (
    (values.roofs?.[additionalInformation?.entityId]?.roofTileLength &&
      values.roofs?.[additionalInformation?.entityId]?.roofTileWidth) ||
    (values.roofs?.[additionalInformation?.entityId]?.tileVisibleLength &&
      values.roofs?.[additionalInformation?.entityId]?.tileVisibleWidth)
  ) {
    return null;
  }
  return 'validation.measurementProtocol.tileMeasurements';
};

export const validateOtherHinderingRoofStructures = (value, values) => {
  if (!values?.otherRoofStructuresExist && !values?.satelliteDishExists) {
    return 'validation.measurementProtocol.otherHinderingRoofStructuresSelect1';
  }
  return null;
};

export const validateGeneratingPlanExists = (
  value,
  values,
  _,
  additionalInformation,
) => {
  if (
    !values.meters?.[additionalInformation?.entityId]?.kkwExists &&
    !values.meters?.[additionalInformation?.entityId]
      ?.balconyPowerPlantExists &&
    !values.meters?.[additionalInformation?.entityId]?.otherGeneratingExists &&
    !values.meters?.[additionalInformation?.entityId]?.photovoltaicExists
  ) {
    return 'validation.measurementProtocol.generatingExistsSelect1';
  }
  return null;
};

const checkFieldNilOrEmptyString = value => {
  return isNil(value) || value === '';
};

export const validateLargeConsumerField =
  (index, validationFn) => (value, values, _, additionalInformation) => {
    const count =
      values.meters?.[additionalInformation?.entityId]?.[
        `largeConsumerCount${index}`
      ];
    const name =
      values.meters?.[additionalInformation?.entityId]?.[
        `largeConsumerName${index}`
      ];
    const power =
      values.meters?.[additionalInformation?.entityId]?.[
        `largeConsumerPower${index}`
      ];

    // Check if ALL fields are nil or empty
    const allFieldsEmpty =
      checkFieldNilOrEmptyString(count) &&
      checkFieldNilOrEmptyString(name) &&
      checkFieldNilOrEmptyString(power);

    if (!allFieldsEmpty) {
      return validationFn(value, values, _, additionalInformation);
    }

    return null;
  };

export const validateInverterField =
  (index, validationFn) => (value, values, _, additionalInformation) => {
    const type =
      values.meters?.[additionalInformation?.entityId]?.[
        `inverterType${index}`
      ];
    const manufacturer =
      values.meters?.[additionalInformation?.entityId]?.[
        `inverterManufacturer${index}`
      ];
    const output =
      values.meters?.[additionalInformation?.entityId]?.[
        `inverterOutput${index}`
      ];

    // Check if ALL fields are nil or empty
    const allFieldsEmpty =
      checkFieldNilOrEmptyString(type) &&
      checkFieldNilOrEmptyString(manufacturer) &&
      checkFieldNilOrEmptyString(output);

    if (!allFieldsEmpty) {
      return validationFn(value, values, _, additionalInformation);
    }

    return null;
  };

export const validateRoofCoveringSpecificType = (
  value,
  values,
  _,
  additionalInformation,
) => {
  if (!value) {
    return 'validation.required';
  }
  const roofCovering =
    values.roofs?.[additionalInformation?.entityId]?.roofCovering;
  const flatRoofCovering =
    values.roofs?.[additionalInformation?.entityId]?.flatRoofCovering;
  const roofType = values.roofs?.[additionalInformation?.entityId]?.roofType;

  if (roofType === ROOF_TYPE.PITCHED) {
    if (
      roofCovering === ROOF_COVERING_OPTIONS.CORRUGATED &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.CORRUGATED
    ) {
      return 'validation.measurementProtocol.corrugatedRoofSpecificType';
    }
    if (
      roofCovering === ROOF_COVERING_OPTIONS.PREFA &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.SCHARE &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.SHINGLES &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.OTHER
    ) {
      return 'validation.measurementProtocol.prefaRoofSpecificType';
    }
    if (
      roofCovering === ROOF_COVERING_OPTIONS.METAL &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.ROUND_SEAM &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.DOUBLE_STANDING_SEAM &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.ANGLED_STANDING_SEAM &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.TRAPEZOIDAL
    ) {
      return 'validation.measurementProtocol.metalSpecificType';
    }
  }

  if (roofType === ROOF_TYPE.FLAT) {
    if (
      flatRoofCovering === FLAT_ROOF_COVERING_OPTIONS.TRAPEZOIDAL_SHEET &&
      value !== ROOF_COVERING_SPECIFIC_TYPE_OPTIONS.TRAPEZOIDAL
    ) {
      return 'validation.measurementProtocol.trapezoidalSpecificType';
    }
  }

  return null;
};

/**
 *
 * @param {boolean} expertMode
 * @param {() => string|null} validationFn
 * @returns
 */
export const validateIfInExpertMode =
  (expertMode, validationFn) => validationFnArgs => {
    if (expertMode) {
      return validationFn(validationFnArgs);
    }
    return null;
  };
/**
 *
 * @param {boolean} expertMode
 * @param {() => string|null} validationFn
 * @returns
 */
export const validateIfNotInExpertMode =
  (expertMode, validationFn) => validationFnArgs => {
    if (expertMode) {
      return null;
    }
    return validationFn(validationFnArgs);
  };
